<script setup lang="ts">
import { useControlId } from '../../composables/useControlId'
import { computed, nextTick, onBeforeUnmount, ref, useSlots, watch, type VNodeChild } from 'vue'
import RenderContent from '../internal/RenderContent'
import { usePopup, type PopupPlacement } from '../../composables/usePopup'
import { useBoundValue } from '../../composables/useBoundValue'

const props = withDefaults(defineProps<{
  title?: VNodeChild
  placement?: PopupPlacement
  trigger?: 'hover' | 'click' | 'focus' | ('hover' | 'click' | 'focus')[]
  open?: boolean
  defaultOpen?: boolean
  mouseEnterDelay?: number
  mouseLeaveDelay?: number
  getPopupContainer?: (trigger: HTMLElement) => HTMLElement
}>(), { placement: 'top', trigger: 'hover', open: undefined, defaultOpen: false, mouseEnterDelay: 0.1, mouseLeaveDelay: 0.1 })
const emit = defineEmits<{ 'update:open': [open: boolean]; openChange: [open: boolean] }>()
const slots = useSlots()
const innerOpen = ref(props.defaultOpen)
const mergedOpen = useBoundValue(props, 'open', innerOpen)
const visible = computed(() => Boolean(mergedOpen.value && (props.title || slots.title)))
const wrapperRef = ref<HTMLElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)
const tooltipId = `rr-tooltip-${useControlId()}`
let timer: ReturnType<typeof setTimeout> | undefined
let describedElement: HTMLElement | null = null
let previousDescription: string | null = null
const triggers = computed(() => Array.isArray(props.trigger) ? props.trigger : [props.trigger])
function clearTimer() { if (timer !== undefined) clearTimeout(timer); timer = undefined }
function setOpen(value: boolean) {
  clearTimer()
  if (value === Boolean(mergedOpen.value)) return
  innerOpen.value = value
  emit('update:open', value)
  emit('openChange', value)
}
function restoreDescription() {
  if (!describedElement) return
  if (previousDescription === null) describedElement.removeAttribute('aria-describedby')
  else describedElement.setAttribute('aria-describedby', previousDescription)
  describedElement = null
}
function describe(element: HTMLElement | null) {
  restoreDescription()
  if (!element) return
  describedElement = element
  previousDescription = element.getAttribute('aria-describedby')
  element.setAttribute('aria-describedby', [previousDescription, tooltipId].filter(Boolean).join(' '))
}
function delayOpen(value: boolean) {
  if (!triggers.value.includes('hover')) return
  clearTimer()
  timer = setTimeout(() => setOpen(value), (value ? props.mouseEnterDelay : props.mouseLeaveDelay) * 1000)
}
function focus(event: FocusEvent) {
  if (triggers.value.includes('focus') || triggers.value.includes('hover')) {
    setOpen(true)
    describe(event.target as HTMLElement)
  }
}
const { container, style, placement } = usePopup(visible, wrapperRef, panelRef, { placement: () => props.placement, getPopupContainer: () => props.getPopupContainer, close: () => setOpen(false), onEscape: () => {} })
watch(visible, async value => {
  if (!value) { restoreDescription(); return }
  await nextTick()
  if (visible.value && !describedElement) describe(wrapperRef.value?.firstElementChild as HTMLElement | null)
})
onBeforeUnmount(() => { clearTimer(); restoreDescription() })
</script>

<template>
  <span ref="wrapperRef" class="rr-tooltip-wrapper" @mouseenter="delayOpen(true)" @mouseleave="delayOpen(false)" @focusin="focus" @focusout="setOpen(false)" @click="triggers.includes('click') && setOpen(!visible)"><slot /></span>
  <Teleport v-if="container" :to="container"><Transition name="rr-fade"><div v-if="visible" :id="tooltipId" ref="panelRef" class="rr-tooltip" :class="`is-${placement}`" :style="style" role="tooltip" @mouseenter="clearTimer" @mouseleave="delayOpen(false)"><slot name="title"><RenderContent :value="title" /></slot></div></Transition></Teleport>
</template>

<style scoped>
.rr-tooltip-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.rr-tooltip {
  position: absolute;
  z-index: var(--rr-z-command, 850);
  width: max-content;
  max-width: 240px;
  min-width: 24px;
  padding: 6px 10px;
  border-radius: var(--rr-radius-control, 6px);
  background: var(--rr-color-text-strong);
  color: var(--rr-color-surface);
  font-size: 12px;
  line-height: 1.4;
  white-space: normal;
  word-break: break-word;
  box-shadow: var(--rr-shadow-overlay);
  pointer-events: auto;
  user-select: text;
}

.rr-tooltip.is-top {
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
}

.rr-tooltip.is-top::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 4px solid transparent;
  border-top-color: var(--rr-color-text-strong);
}

.rr-tooltip.is-bottom {
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
}

.rr-tooltip.is-bottom::after {
  content: '';
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 4px solid transparent;
  border-bottom-color: var(--rr-color-text-strong);
}

.rr-tooltip.is-left {
  right: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
}

.rr-tooltip.is-left::after {
  content: '';
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  border: 4px solid transparent;
  border-left-color: var(--rr-color-text-strong);
}

.rr-tooltip.is-right {
  left: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
}

.rr-tooltip.is-right::after {
  content: '';
  position: absolute;
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
  border: 4px solid transparent;
  border-right-color: var(--rr-color-text-strong);
}
</style>
